import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { Project } from '../types/tasks'
import { useProjectsApi } from './projectsApi'
import { useQueryEnabled } from 'app/utils/query/useQueryEnabled'

// hook to retrieve all projects
export const useProjects = () => {
  const { getProjects } = useProjectsApi()
  const isQueryEnabled = useQueryEnabled()

  return useQuery<Project[], Error>({
    queryKey: ['projects', 'all'],
    queryFn: async () => {
      let allProjects: Project[] = []
      let cursor: string | undefined

      try {
        while (true) {
          const response = await getProjects(cursor, 100, null, false)
          allProjects = [...allProjects, ...response.items]
          if (response.items.length < 100) break
          cursor = response.next_page
        }
        return allProjects
      } catch (error) {
        console.error('Error fetching projects:', error)
        throw new Error('Failed to fetch projects')
      }
    },
    enabled: isQueryEnabled,
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    refetchOnWindowFocus: true
  })
}

// Hook to retrieve deleted projects
export const useDeletedProjects = () => {
  const { getProjects } = useProjectsApi()
  const isQueryEnabled = useQueryEnabled()

  return useQuery<Project[], Error>({
    queryKey: ['projects', 'deleted'],
    queryFn: async () => {
      let deletedProjects: Project[] = []
      let cursor: string | undefined

      try {
        while (true) {
          const response = await getProjects(cursor, 100, null,true)
          const deletedItems = response.items.filter(project => project.is_deleted)
          deletedProjects = [...deletedProjects, ...deletedItems]
          if (response.items.length < 100) break
          cursor = response.next_page
        }
        return deletedProjects
      } catch (error) {
        console.error('Error fetching deleted projects:', error)
        throw new Error('Failed to fetch deleted projects')
      }
    },
    enabled: isQueryEnabled,
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true
  })
}


// Hook to retrieve only active projects
export const useActiveProjects = () => {
  const { getProjects } = useProjectsApi()
  const isQueryEnabled = useQueryEnabled()

  return useQuery<Project[], Error>({
    queryKey: ['projects', 'active'],
    queryFn: async () => {
      let activeProjects: Project[] = []
      let cursor: string | undefined

      try {
        while (true) {
          const response = await getProjects(cursor, 100, 'incomplete', false)
          activeProjects = [...activeProjects, ...response.items]
          if (response.items.length < 100) break
          cursor = response.next_page
        }
        return activeProjects
      } catch (error) {
        console.error('Error fetching active projects:', error)
        throw new Error('Failed to fetch active projects')
      }
    },
    enabled: isQueryEnabled,
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    refetchOnWindowFocus: true
  })
}
